/* UFORIQ per-location stock limits (2026-10-17). Browser side of assets/uq-stock-logic.js.
 *
 * A shopper can never put more of a variant in the cart than the location that will fulfill the order
 * has (Central Texas for Austin delivery, West Texas for Lubbock delivery, both for shipping). Same rule
 * as the iOS app. No area chosen = no extra limit.
 *
 * Where the numbers come from:
 *   1. The variant metafield uforiq.location_stock, kept current by the uforiq-ops app. Liquid renders
 *      it into the page for tracked variants that can't oversell:
 *        - product forms:   snippets/uq-stock-data.liquid  (<script type="application/json" data-uq-stock>)
 *        - drawer lines:    sections/uq-cart-drawer.liquid (data JSON, "stock" per line)
 *        - drawer cards:    sections/uq-cart-products.liquid (data-uq-stock on each card)
 *   2. When Theme settings > UFORIQ cart drawer > "Storefront API public token" is set, fresher numbers
 *      from the Storefront API (metafield first, storeAvailability only if the metafield is missing),
 *      cached for 60 s. Only variants the page already marked as limited are looked up.
 * A failed or slow lookup (over 2.5 s) never blocks a shopper: the last known numbers stay, or no limit.
 *
 * Public: window.UqStock (used by assets/uq-cart-drawer.js). Fires `uq:stock:change` on window whenever
 * the area, the method, the cart or the numbers change.
 */
(function () {
  'use strict';

  var S = window.UqStockLogic;
  if (!S) return;

  var API_VERSION = '2026-10';
  var CACHE_MS = 60000;
  var TIMEOUT_MS = 2500;
  var cfg = window.UqStockConfig || {};
  var TOKEN = (cfg.token || '').trim();
  var ENDPOINT = cfg.endpoint || ('https://uforiq.myshopify.com/api/' + API_VERSION + '/graphql.json');

  var registry = {};      // variantId -> stock object or null (tracked, can't oversell)
  var packs = {};         // pack variantId -> { of: single variantId, qty } (metafield uforiq.pack)
  var apiCache = {};      // variantId -> { stock, at }
  var inflight = null;
  var cartLines = null;   // [{ key, variantId, qty, addon }]
  var cartAttrs = {};
  var lastSig = '';

  /* ---------------- area + method ---------------- */

  function getArea() {
    var a = null;
    try {
      if (window.UforiqLocation && typeof window.UforiqLocation.get === 'function') a = window.UforiqLocation.get();
      if (!a && window.UforiqArea) a = window.UforiqArea.get(); // area in effect (2026-11-02, visit areas)
      if (!a) {
        var raw = localStorage.getItem('uforiq.location');
        a = raw ? JSON.parse(raw).location : null;
      }
      if (!a) a = localStorage.getItem('selected-store');
    } catch (e) { a = null; }
    return S.normalizeArea(a);
  }

  function getRequestedMethod() {
    var m = null;
    try {
      var sch = window.UforiqSchedule;
      if (sch && typeof sch.getState === 'function') m = sch.getState().method;
    } catch (e) { m = null; }
    if (!m) {
      try {
        var saved = JSON.parse(sessionStorage.getItem('uqs:v1') || 'null');
        if (saved && saved.method) m = saved.method;
      } catch (e) { /* private mode */ }
    }
    if (!m && cartAttrs && cartAttrs['Checkout-Method']) m = cartAttrs['Checkout-Method'];
    return m === 'delivery' || m === 'shipping' ? m : null;
  }

  function target() { return S.resolveTarget(getArea(), getRequestedMethod()); }

  /* ---------------- stock registry ---------------- */

  function register(map) {
    if (!map || typeof map !== 'object') return;
    Object.keys(map).forEach(function (id) {
      var e = map[id];
      registry[String(id)] = e && e.stock ? S.parseStock(e.stock) : null;
      var p = e && e.pack ? S.parsePack(e.pack) : null;
      if (p) packs[String(id)] = p; else delete packs[String(id)];
    });
  }

  function scan(rootEl) {
    var els = (rootEl || document).querySelectorAll('[data-uq-stock]');
    Array.prototype.forEach.call(els, function (el) {
      var raw = el.tagName === 'SCRIPT' ? el.textContent : el.getAttribute('data-uq-stock');
      if (!raw) return;
      try { register(JSON.parse(raw)); } catch (e) { /* ignore a bad block */ }
    });
  }

  function isLimited(id) { return Object.prototype.hasOwnProperty.call(registry, String(id)); }

  function stockOf(id) {
    id = String(id);
    if (!isLimited(id)) return null;
    var c = apiCache[id];
    if (c && c.stock && Date.now() - c.at < CACHE_MS * 5) return c.stock;
    return registry[id];
  }

  function limitOf(id, t) { return S.limitFor(stockOf(id), t === undefined ? target() : t); }

  function packOf(id) { return packs[String(id)] || null; }
  function unitOf(id, t) { return S.unitFor(id, stockOf, t === undefined ? target() : t, packOf); }

  /* ---------------- Storefront API ---------------- */

  var QUERY = 'query UqStock($ids: [ID!]!) { nodes(ids: $ids) { ... on ProductVariant { id quantityAvailable ' +
    'metafield(namespace: "uforiq", key: "location_stock") { value } ' +
    'storeAvailability(first: 5) { nodes { quantityAvailable location { id } } } } } }';

  /* Resolves when fresh numbers are in, or right away when there is nothing to do; never rejects. */
  function refresh(ids, opts) {
    opts = opts || {};
    if (!TOKEN || !window.fetch) return Promise.resolve(false);
    var now = Date.now();
    // A pack's limit comes from its single, so look the single up too.
    ids = (ids || []).map(String);
    ids = ids.concat(ids.map(function (id) { return packs[id] ? packs[id].of : null; }).filter(Boolean));
    var want = ids.filter(function (id, i, a) {
      if (a.indexOf(id) !== i || !isLimited(id)) return false;
      var c = apiCache[id];
      return opts.force || !c || now - c.at >= CACHE_MS;
    });
    if (!want.length) return inflight || Promise.resolve(false);
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer;
    var timeout = new Promise(function (resolve) { timer = setTimeout(function () { if (ctrl) ctrl.abort(); resolve(false); }, TIMEOUT_MS); });
    var req = window.fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN },
      body: JSON.stringify({ query: QUERY, variables: { ids: want.slice(0, 250).map(S.variantGid) } }),
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (r) { return r.ok ? r.json() : null; }).then(function (json) {
      var nodes = json && json.data && json.data.nodes;
      if (!nodes) return false;
      var at = Date.now();
      nodes.forEach(function (n) {
        if (!n || !n.id) return;
        var id = S.variantNumericId(n.id);
        var stock = S.stockFromNode(n);
        // Unknown from the API (no metafield, no storeAvailability): keep what the page had.
        apiCache[id] = { stock: stock, at: at };
      });
      return true;
    }).catch(function () { return false; });
    var p = Promise.race([req, timeout]).then(function (ok) {
      clearTimeout(timer);
      if (inflight === p) inflight = null;
      if (ok) changed(true);
      return ok;
    });
    inflight = p;
    return p;
  }

  /* ---------------- cart ---------------- */

  function setCart(lines, attributes) {
    cartLines = (lines || []).map(function (l) {
      if (l.limited) {
        var m = {};
        m[String(l.variantId)] = { stock: l.stock, pack: l.pack };
        register(m);
      }
      return { key: l.key, variantId: l.variantId, qty: l.qty, addon: !!l.addon };
    });
    cartAttrs = attributes || {};
    changed();
  }

  function cartQty(id) {
    id = String(id);
    return (cartLines || []).reduce(function (n, l) { return n + (String(l.variantId) === id && !l.addon ? (Number(l.qty) || 0) : 0); }, 0);
  }

  function ensureCart() {
    if (cartLines) return Promise.resolve(cartLines);
    return window.fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (c && c.items && !cartLines) {
          cartLines = c.items.map(function (i) { return { key: i.key, variantId: i.variant_id, qty: i.quantity, addon: false }; });
          cartAttrs = c.attributes || {};
        }
        return cartLines || [];
      })
      .catch(function () { return cartLines || []; });
  }

  /* Singles in the cart that count against `base`'s stock (packs count qty x singles). */
  function unitsInCart(base, t) {
    return (cartLines || []).reduce(function (n, l) {
      if (l.addon) return n;
      var u = unitOf(l.variantId, t);
      return u.base === base ? n + (Number(l.qty) || 0) * u.mult : n;
    }, 0);
  }

  /* -> verdict for adding `qty` of a variant now: { allowed, capped, remaining, limit, message }.
   * `limit` is how many of this variant the cart can hold given everything else in it. */
  function checkAdd(id, qty) {
    var t = target();
    var u = unitOf(id, t);
    var baseLimit = limitOf(u.base, t);
    var mine = cartQty(id);
    var limit = null;
    if (baseLimit !== null) {
      var others = unitsInCart(u.base, t) - mine * u.mult;
      limit = Math.max(0, Math.floor((baseLimit - others) / u.mult));
    }
    var r = S.capAdd(qty, mine, limit);
    r.message = r.limit === null ? '' : S.message(t, r.limit);
    return r;
  }

  function evaluate(lines) { return S.evaluateLines(lines || cartLines || [], stockOf, target(), packOf); }

  /* ---------------- change notifications ---------------- */

  function sig() {
    var t = target();
    return (t ? t.key + ':' + t.method + ':' + t.area : 'none') + '|' +
      (cartLines || []).map(function (l) { return l.variantId + 'x' + l.qty; }).join(',');
  }

  function changed(force) {
    var s = sig();
    if (!force && s === lastSig) return;
    lastSig = s;
    var ev;
    try { ev = new CustomEvent('uq:stock:change', { detail: { target: target() } }); } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent('uq:stock:change', false, false, { target: target() });
    }
    window.dispatchEvent(ev);
  }

  /* ---------------- product forms (product page, quick view, featured product, quick buy) ---------------- */

  function productForms() {
    return Array.prototype.slice.call(document.querySelectorAll('form[action*="/cart/add"]')).filter(function (f) {
      return !f.closest('[data-uq-cart]') && f.querySelector('[name="id"]');
    });
  }

  function qtyInput(form) {
    var q = form.querySelector('input[name="quantity"]');
    if (q) return q;
    if (form.id) q = document.querySelector('input[name="quantity"][form="' + form.id + '"]');
    if (q) return q;
    var scope = form.closest('[data-js-product-component]');
    return scope ? scope.querySelector('input[name="quantity"]') : null;
  }

  function submitButtons(form) {
    var list = Array.prototype.slice.call(form.querySelectorAll('[type="submit"], [data-js-product-add-to-cart]'));
    if (form.id) list = list.concat(Array.prototype.slice.call(document.querySelectorAll('[type="submit"][form="' + form.id + '"]')));
    return list.filter(function (b, i, a) { return a.indexOf(b) === i && !b.closest('.shopify-payment-button'); });
  }

  function noteFor(form, create) {
    var note = form.querySelector('[data-uq-stock-note]');
    if (!note && create) {
      note = document.createElement('p');
      note.className = 'uq-stock-note';
      note.setAttribute('data-uq-stock-note', '');
      note.setAttribute('role', 'status');
      note.setAttribute('aria-live', 'polite');
      form.appendChild(note);
    }
    return note;
  }

  function applyForm(form) {
    var idEl = form.querySelector('[name="id"]');
    var id = idEl && idEl.value;
    var q = qtyInput(form);
    var btns = submitButtons(form);
    var r = id && isLimited(id) ? checkAdd(id, 1) : null;
    var blocked = !!(r && r.limit !== null && r.remaining <= 0);

    btns.forEach(function (b) {
      if (blocked) {
        b.classList.add('uq-stock-blocked');
        b.setAttribute('aria-disabled', 'true');
        b.setAttribute('data-uq-stock-blocked', '');
      } else if (b.hasAttribute('data-uq-stock-blocked')) {
        b.classList.remove('uq-stock-blocked');
        b.removeAttribute('aria-disabled');
        b.removeAttribute('data-uq-stock-blocked');
      }
    });

    if (q) {
      if (!q.hasAttribute('data-uq-max-orig')) q.setAttribute('data-uq-max-orig', q.getAttribute('max') || '');
      if (r && r.limit !== null && r.remaining > 0) {
        q.setAttribute('max', String(r.remaining));
        if ((parseInt(q.value, 10) || 1) > r.remaining) q.value = String(r.remaining);
      } else {
        var orig = q.getAttribute('data-uq-max-orig');
        if (orig) q.setAttribute('max', orig); else q.removeAttribute('max');
      }
    }

    var note = noteFor(form, blocked);
    if (note) {
      var text = blocked ? r.message : '';
      if (note.textContent !== text) note.textContent = text;
      note.hidden = !blocked;
    }
  }

  var applyTimer = null;
  function applyForms() {
    clearTimeout(applyTimer);
    applyTimer = setTimeout(function () {
      scan();
      productForms().forEach(applyForm);
    }, 30);
  }

  /* ---------------- wiring ---------------- */

  function init() {
    scan();
    ensureCart().then(function () { changed(true); });
    var ids = Object.keys(registry);
    if (ids.length) refresh(ids);

    window.addEventListener('uq:stock:change', applyForms);
    window.addEventListener('uforiq:location:changed', function () { changed(); });
    window.addEventListener('uforiq:schedule:change', function () { changed(); });
    window.addEventListener('storage', function (e) {
      if (!e.key || e.key === 'uforiq.location' || e.key === 'selected-store') changed();
    });
    // Krown fires VARIANT_CHANGE (not bubbling) on <product-variants> after it swaps the variant id.
    document.addEventListener('VARIANT_CHANGE', applyForms, true);
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('form[action*="/cart/add"], product-variants')) applyForms();
    }, true);
    // Quick view and other forms that arrive later.
    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType === 1 && (n.matches('form[action*="/cart/add"], [data-uq-stock]') || n.querySelector('form[action*="/cart/add"], [data-uq-stock]'))) {
              applyForms();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
    applyForms();
  }

  window.UqStock = {
    ready: true,
    hasToken: !!TOKEN,
    target: target,
    getArea: getArea,
    getRequestedMethod: getRequestedMethod,
    isLimited: isLimited,
    stockOf: stockOf,
    limitOf: limitOf,
    packOf: packOf,
    checkAdd: checkAdd,
    evaluate: evaluate,
    refresh: refresh,
    setCart: setCart,
    cartQty: cartQty,
    scan: scan,
    message: function (n) { return S.message(target(), n); },
    applyForms: applyForms
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
